import { INotification } from '@components/common/NotifyFirebase/WapperNotification';
import UtilOrder from '@pages/OrganizedSales/CreateOrders/utilOrder';
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
  Update
} from '@reduxjs/toolkit';
import NotificationApi from 'apis/api/NotificationApi';
import OrderManagerApi from 'apis/api/OrderManager';
import Helpers from 'helpers/Helpers';
import { ICustomer } from 'models/Customer';
import { IOrderManager } from 'models/OrderManager';
import { IProductCategory } from 'models/StockManage/CateProductManage';

// ===================== NOTIFICATION HISTORY (lịch sử thông báo backend) =====================
export type NotificationTag = 'Order' | 'ProductTransaction';

export interface NotificationHistoryItem {
  id: number;
  title: string;
  body: string;
  createDate: string;
  tag: NotificationTag;
  identifiers: string[]; // Order: mã đơn hàng | ProductTransaction: id lệnh giao dịch
  isRead: boolean;
}

// Modal "xem tất cả": chế độ REPLACE theo trang (khác getNotiHistory là append)
export const getNotiHistoryModal = createAsyncThunk(
  'orderManager/getNotiHistoryModal',
  ({ pageIndex = 1, pageSize = 20 }: { pageIndex?: number; pageSize?: number } = {}) => {
    return NotificationApi.GetNotifications(pageIndex, pageSize).then(res => ({
      ...res.data.data,
      _pageSize: pageSize
    }));
  }
);
// res.data = envelope { data, message, error } (giống getDetailOfOrder) -> res.data.data = phân trang
export const getNotiHistory = createAsyncThunk(
  'orderManager/getNotiHistory',
  ({ pageIndex = 1, pageSize = 10 }: { pageIndex?: number; pageSize?: number } = {}) => {
    return NotificationApi.GetNotifications(pageIndex, pageSize).then(res => ({
      ...res.data.data,
      _pageSize: pageSize
    }));
  }
);

export const readNoti = createAsyncThunk('orderManager/readNoti', (id: number) => {
  return NotificationApi.ReadNotification(id).then(() => id);
});

// Tổng số chưa đọc từ server. Payload: số, hoặc null khi lỗi (giữ cách đếm theo trang đã tải)
export const getUnreadNotiAmount = createAsyncThunk('orderManager/getUnreadNotiAmount', () => {
  return NotificationApi.GetAllUnreadAmount().then((res: any) => {
    if (res?.data?.error) return null;
    const d = res?.data?.data;
    const n = Number(typeof d === 'object' && d !== null ? d.amount ?? d.total ?? d.count : d);
    return Number.isFinite(n) ? n : null;
  });
});

export const readAllNotis = createAsyncThunk('orderManager/readAllNotis', () => {
  return NotificationApi.ReadAllNotifications();
});

// Bấm 1 đơn trong thông báo:
//   B1: gọi api/Order/GetDetail (endpoint hỗn hợp) chỉ để lấy orderType.id
//   B2: truyền typeOrder đó vào getDetailOfOrder (đúng luồng format + mở modal cũ)
export const openOrderDetailFromNoti = createAsyncThunk(
  'orderManager/openOrderDetailFromNoti',
  async (maDonHang: string, { dispatch }) => {
    let orderTypeId: number | undefined;
    try {
      const res = await NotificationApi.GetOrderDetail(maDonHang);
      // res.data = envelope { data, message, error } -> res.data.data.orderType.id
      orderTypeId = res?.data?.data?.orderType?.id;
    } catch (e) {
      console.error('GetDetail lấy orderType lỗi:', e);
    }
    await dispatch(getDetailOfOrder({ maDonHang, typeOrder: orderTypeId as number, forceOpen: true }));
    return { maDonHang, orderTypeId };
  }
);

export const getDetailOfOrder = createAsyncThunk(
  'select/getDetailOfOrder',
  (query: { maDonHang: string; typeOrder: number; forceOpen?: boolean }) => {
    return OrderManagerApi.GetDetailOfOrder(query.maDonHang, query.typeOrder).then(response => {
      const reqData = { ...response.data, forceOpen: query.forceOpen };
      return reqData;
    });
  }
);
export function sortByTimeUpdate(a: IOrderManager, b: IOrderManager): number {
  return new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime();
}

const allOrderAdapter = createEntityAdapter<IOrderManager>({
  selectId: order => order?.maDonHang,
  sortComparer: sortByTimeUpdate
});

export function sortByTimeUpdateCustomer(a: ICustomer, b: ICustomer): number {
  return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
}

const allCustomerAdapter = createEntityAdapter<ICustomer>({
  selectId: customer => customer.maKhachHang,
  sortComparer: sortByTimeUpdateCustomer
});
type ICountOrders = {
  UnSuccesCount?: number;
  ConfirmPaymentCount?: number;
  ConfirmWaitCount?: number;
  InProcessCount?: number;
  InHoldCount?: number;
  SuccessCount?: number;
  CancelCount?: number;
  PauseCount?: number;
  AllCount?: number;
};

type initialStateType = {
  countOrders: ICountOrders;
  orderData: Array<IOrderManager>;
  dataTableListOrder: Array<IProductCategory>;
  listProductSelectFixed: Array<IProductCategory>;
  customerSelect?: ICustomer;
  orderSelect?: IOrderManager | null;
  orderCreateBase?: IOrderManager | null;
  orderCreateByJson?: any | null;
  requestReGetOrder?: boolean | null;
  requestReGetCustomer?: boolean | null;
  draftOrders?: IOrderManager[];
  createOrders?: IOrderManager[] | any;
  allOrderData?: EntityState<IOrderManager>;
  allCustomerData?: EntityState<ICustomer>;
  detailBase?: any[];
  detailErrors?: any[];
  detailThuNos?: any[];
  detailChenhLechs?: any[];
  detailDLLG?: any[];
  detailTraHang?: any[];
  detailUpgrades?: any[];
  // productUpgrades?: any[];
  detailPayFees?: any[];
  detailReplace?: any[];
  // liên quan đến các thao tác thuật tiện cho đơn hàng
  listNotification?: Array<INotification>; // ds các thông báo đã nhận
  listHistoryNotis?: Array<INotification>; // ds lịch sử các thông báo chuyển từ đã nhận sang lưu lại khi cần dò thông báo cũ
  notification?: INotification | null;
  openHistoryNoti?: boolean;
  // Lịch sử thông báo lấy từ backend (KHÁC listHistoryNotis là ds real-time lưu tạm)
  notiHistory?: {
    items: NotificationHistoryItem[];
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
    hasNextPage: boolean;
    unreadCount: number;
    /** đã lấy được số chưa đọc từ server (GetAllUnreadAmount) -> không tính lại theo trang đã tải */
    unreadFromServer?: boolean;
    loading: boolean;
    loadingMore: boolean;
  };
  notiHistoryModal?: {
    open: boolean;
    items: NotificationHistoryItem[];
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
    loading: boolean;
  };

  isACNotify?: boolean;
  openQActionOrder?: boolean;
  openUpdateOrder?: boolean;
  openCreateOrderModal?: boolean;
  openCodeJsonModal?: boolean;
  openCodeDraftModal?: boolean;
  openCodeCreateModal?: boolean;
  payedMoney?: number;
  payedBankingMoney?: number; // tiền chuyển khoản thực tế
  payWillMoney?: number;
  payMethod?: string;
  vatValueMoney?: number;
  otherCost?: number;
  // ariseCost?: number;
  timePostPone?: Date | null;
  requestExportXls?: string | null;
  reqResetCreateForm?: boolean;
  listSameCus?: any[]; // các kh trùng nhau trong ds đơn
  codeProductQuick?: string | null;
  donTaoLai?: any | null;
  taoCongThuNo?: any | null;
  taoDonChietKhau?: any | null;
  omConfig?: any[];

  // yêu cầu edit đơn nếu đơn đã hoàn tất
  isReqEditOrder?: boolean;
};

const initialState: initialStateType = {
  countOrders: {
    UnSuccesCount: 0,
    ConfirmPaymentCount: 0,
    ConfirmWaitCount: 0,
    InProcessCount: 0,
    InHoldCount: 0,
    SuccessCount: 0,
    PauseCount: 0,
    CancelCount: 0,
    AllCount: 0
  },
  orderData: [],
  dataTableListOrder: [], // k còn dùng nữa chuyển qua lưu zustand rồi
  listProductSelectFixed: [], // k còn dùng nữa chuyển qua lưu zustand rồi
  customerSelect: undefined,
  orderSelect: null, // lưu đơn đang cần edit
  orderCreateBase: null, // đơn lấy dữ liệu khách hàng để đưa vào đơn mới tạo nhanh... sẽ phát triển thêm...
  requestReGetOrder: null, // bắt buộc là null để đúng với logic xử lý state của use query
  requestReGetCustomer: null, // bắt buộc là null để đúng với logic xử lý state của use query
  allOrderData: allOrderAdapter.getInitialState(),
  allCustomerData: allCustomerAdapter.getInitialState(),
  detailBase: [],
  detailErrors: [],
  detailThuNos: [],
  detailChenhLechs: [],
  detailDLLG: [],
  detailTraHang: [],
  detailUpgrades: [],
  // productUpgrades: [],
  detailPayFees: [],
  detailReplace: [],
  listNotification: [],
  listHistoryNotis: [],
  notification: null,
  isACNotify: true,
  openQActionOrder: false,
  openUpdateOrder: false,
  orderCreateByJson: null,
  openCreateOrderModal: false,
  openCodeJsonModal: false,
  openCodeDraftModal: false,
  openCodeCreateModal: false,
  payedMoney: 0,
  payedBankingMoney: 0,
  payWillMoney: 0,
  otherCost: 0,
  vatValueMoney: 0,
  // ariseCost: 0,
  requestExportXls: null,
  reqResetCreateForm: false,
  listSameCus: [],
  openHistoryNoti: false,
  notiHistory: {
    items: [],
    pageIndex: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
    hasNextPage: false,
    unreadCount: 0,
    loading: false,
    loadingMore: false
  },
  notiHistoryModal: {
    open: false,
    items: [],
    pageIndex: 1,
    pageSize: 20,
    totalPages: 0,
    totalRecords: 0,
    loading: false
  },

  timePostPone: null,
  codeProductQuick: null,
  payMethod: null,
  isReqEditOrder: false,
  donTaoLai: null,
  taoCongThuNo: null,
  taoDonChietKhau: null
};

const orderManagerSlide = createSlice({
  name: 'orderManager',
  initialState: initialState,
  reducers: {
    resetState: state => {
      state.orderData = [];
      state.orderSelect = undefined;
      state.orderCreateBase = undefined;
    },
    reqCalculate: (state, action: PayloadAction<boolean>) => {
      const list: any = allOrderAdapter.getSelectors().selectAll(state.allOrderData);
      const rltCount = UtilOrder.countOrders(list);
      // console.log('rltCount in store', rltCount);
      state.countOrders = rltCount;
    },
    updateCountOrder: (state, action: PayloadAction<ICountOrders>) => {
      state.countOrders = action.payload;
    },
    updateReqReGetOrder: (state, action: PayloadAction<boolean>) => {
      state.requestReGetOrder = action.payload;
    },
    updateReqReGetCustomer: (state, action: PayloadAction<boolean>) => {
      // console.log('updateReqReGetCustomer', action.payload);
      state.requestReGetCustomer = action.payload;
    },
    updateReqExportXls: (state, action: PayloadAction<string | null>) => {
      switch (action.payload) {
        case '1':
          state.requestExportXls = 'tatcadonhang';
          break;
        case 'ConfirmWait':
          state.requestExportXls = 'donchuahoantat';
          break;
        case '3':
          state.requestExportXls = 'donchoxacnhan';
          break;
        case 'InProcess':
          state.requestExportXls = 'dondangthuchien';
          break;
        case 'InHold':
          state.requestExportXls = 'donchotamhoan';
          break;
        case 'Success':
          state.requestExportXls = 'donhoantat';
          break;
        case 'ConfirmPayment':
          state.requestExportXls = 'dondaxacnhanthutien';
          break;
        case '2':
          state.requestExportXls = 'donchuahoantat';
          break;
        case '4':
          state.requestExportXls = 'dondangthuchien';
          break;
        case '5':
          state.requestExportXls = 'donchotamhoan';
          break;
        case '6':
          state.requestExportXls = 'donhoantat';
          break;
        default:
          state.requestExportXls = action.payload;
          break;
      }
    },
    resetDataListDataTable: state => {
      state.dataTableListOrder = [];
      state.listProductSelectFixed = [];
    },
    updateDataTableListOrder: (state, action: PayloadAction<Array<IProductCategory>>) => {
      state.dataTableListOrder = action.payload;
    },
    updateListProductSelectFixed: (state, action: PayloadAction<Array<IProductCategory>>) => {
      state.listProductSelectFixed = action.payload;
    },
    updateCustomerSelect: (state, action: PayloadAction<ICustomer | undefined>) => {
      state.customerSelect = action.payload;
    },
    updateAllOrderData: (state, action: PayloadAction<Array<IOrderManager>>) => {
      //  thay cho setAll là dùng để cập nhật thêm (đã có thì update chưa có thì thêm vô) => sẽ rất hiệu quả để cập nhật data
      // tuy nhiên đối với table 2 bước gọi api thì k thể dùng upsert!!, mọi cập nhật đơn đều phải qua action: updateOneOrderData

      if (state.requestReGetOrder === true) {
        allOrderAdapter.setAll(state.allOrderData, action.payload); // k cần xử lý nữa
        state.requestReGetOrder = false; // tắt state update !
      } else {
        allOrderAdapter.setAll(state.allOrderData, action.payload); // k cần xử lý nữa
      }
      if (state.orderSelect) {
        const found = allOrderAdapter.getSelectors().selectById(state.allOrderData, state.orderSelect!.maDonHang)!;
        // Modal đơn đang mở (vd mở từ thông báo) mà đơn không có trong list vừa tải -> giữ đơn đang chọn,
        // nếu không modal đọc orderSelect undefined -> lỗi "reading 'orderType'"
        state.orderSelect = found || (state.openUpdateOrder ? state.orderSelect : found);
      }
    },
    updateAllCustomerData: (state, action: PayloadAction<Array<ICustomer>>) => {
      //  thay cho setAll là dùng để cập nhật thêm (đã có thì update chưa có thì thêm vô) => sẽ rất hiệu quả để cập nhật data
      // tuy nhiên đối với table 2 bước gọi api thì k thể dùng upsert!!, mọi cập nhật đơn đều phải qua action: updateOneOrderData
      if (state.requestReGetCustomer === true) {
        allCustomerAdapter.setAll(state.allCustomerData, action.payload); // k cần xử lý nữa
        state.requestReGetCustomer = false; // tắt state update !
      } else {
        allCustomerAdapter.setAll(state.allCustomerData, action.payload); // k cần xử lý nữa
      }
      if (state.customerSelect) {
        state.customerSelect = allCustomerAdapter
          .getSelectors()
          .selectById(state.allCustomerData, state.customerSelect!.maKhachHang)!;
      }
    },
    updateManyOrderData: (state, action: PayloadAction<Array<Update<IOrderManager>>>) => {
      allOrderAdapter.updateMany(state.allOrderData, action.payload);
      // sau đó cập nhật cho xe được chọn để hiện thông tin chi tiết info
      if (state.orderSelect) {
        const found = allOrderAdapter.getSelectors().selectById(state.allOrderData, state.orderSelect!.maDonHang)!;
        state.orderSelect = found || (state.openUpdateOrder ? state.orderSelect : found); // xem updateAllOrderData
      }
      if (state.orderCreateBase) {
        state.orderCreateBase = allOrderAdapter
          .getSelectors()
          .selectById(state.allOrderData, state.orderSelect!.maDonHang)!;
      }
    },
    // chỉ dùng nội bộ trong slice store
    updateOneOrderData: (state, action: PayloadAction<Update<IOrderManager>>) => {
      // VỪA UPDATE VÀO LIST DATA VÀ VỪA CẬP NHẬT VÀO CÁI ĐƠN ĐANG SELECT...
      const dataChange: any = action.payload?.changes;
      dataChange && allOrderAdapter.upsertOne(state.allOrderData, dataChange);
    },
    // dùng ra ngoài
    updateOneOrderOnlyData: (state, action: PayloadAction<Update<IOrderManager>>) => {
      // chỉ update data vào list k tiến hành thao tác thêm gì khác tránh render bla bla..
      const dataChange: any = action.payload?.changes;
      dataChange && allOrderAdapter.upsertOne(state.allOrderData, dataChange);
    },
    addOneOrderInTopList: (state, action: PayloadAction<Array<IOrderManager>>) => {
      const list: any = allOrderAdapter.getSelectors().selectAll(state.allOrderData);
      const newList: any = [...action.payload, ...list];
      allOrderAdapter.setAll(state.allOrderData, newList); // set data mới lên đầu tiên của list cái nào đã có sẽ được gộp lấy cái mới nhất
      state.requestReGetOrder = false; // tắt state update !
    },
    addOneCustomerInTopList: (state, action: PayloadAction<Array<ICustomer>>) => {
      const list: any = allCustomerAdapter.getSelectors().selectAll(state.allCustomerData);
      const newList: any = [...action.payload, ...list];
      // console.log('newList', newList);
      allCustomerAdapter.setAll(state.allCustomerData, newList); // set data mới lên đầu tiên của list cái nào đã có sẽ được gộp lấy cái mới nhất
    },
    pushOrderInTopList: (state, action: PayloadAction<Array<IOrderManager>>) => {
      // lọc ra các đơn chưa có trong list rồi mới upsert

      const listNew: any = [];
      for (let i = 0; i < action.payload.length; i++) {
        let findExist: any = allOrderAdapter
          .getSelectors()
          .selectById(state.allOrderData, action.payload[i]?.maDonHang);
        if (!findExist) {
          // tìm không có thì push vô
          listNew.push(action.payload[i]);
        }
        // tìm có thì coi trạng thái đơn hàng và ưu tiên đơn hay không có khác cũ và mới thì cập nhật lại..
        if (
          findExist &&
          (findExist.isPrioritized != action.payload[i].isPrioritized ||
            findExist.currentStatus != action.payload[i].currentStatus ||
            findExist.operatorSeen != action.payload[i].operatorSeen ||
            findExist.yeuCauChinhSua != action.payload[i].yeuCauChinhSua ||
            findExist.orderType.id != action.payload[i].orderType.id)
        ) {
          findExist = { ...findExist };
          findExist['isPrioritized'] = action.payload[i].isPrioritized;
          findExist['currentStatus'] = action.payload[i].currentStatus;
          findExist['operatorSeen'] = action.payload[i].operatorSeen;
          findExist['yeuCauChinhSua'] = action.payload[i].yeuCauChinhSua;
          findExist['orderType'] = action.payload[i].orderType;

          const rltFormat = findExist.isDetailLoaded
            ? UtilOrder.formatOneDataOrder(findExist)
            : UtilOrder.formatVarOrder(findExist);
          listNew.push(rltFormat);
        }
      }
      // console.log('listNew', listNew);
      listNew.length != 0 && allOrderAdapter.upsertMany(state.allOrderData, listNew);
    },
    updateCreateBaseOrder: (state, action: PayloadAction<string | null>) => {
      if (action.payload === null) {
        state.orderCreateBase = null;
      } else {
        const orderSearch = allOrderAdapter.getSelectors().selectById(state.allOrderData, action.payload)!;
        // tìm device đã chọn theo plate và add nó vào xe chọn
        if (orderSearch) {
          state.orderCreateBase = { ...orderSearch };
        } else {
          state.orderCreateBase = null;
        }
      }
    },
    // set đang đang db click => set trước rồi đổ thêm data
    updateOrderSelected: (state, action: PayloadAction<string | null>) => {
      // state.orderCreateBase = null;
      if (action.payload === null) {
        state.orderSelect = null;
      } else {
        const orderSearch = allOrderAdapter.getSelectors().selectById(state.allOrderData, action.payload)!;
        console.log('selectedRealtimeDeviceTreeview', orderSearch);
        // tìm device đã chọn theo plate và add nó vào xe chọn
        if (orderSearch) {
          state.orderSelect = { ...orderSearch };
        } else {
          // trường hợp tìm k ra thì phải xử lý gọi api theo mã đơn hàng để lấy đơn đó về rồi add vô list đơn và bật select đơn
          //  bắt buộc phải xử lý tách biệt k phụ thuộc trang nào để linh động việc xem chi tiết đơn
          state.orderSelect = null;
        }
      }
    },
    forceUpdateOrderClick: (state, action: PayloadAction<any>) => {
      // state.orderCreateBase = null;
      if (action.payload === null) {
        state.orderSelect = null;
      } else {
        state.orderSelect = { ...action.payload };
        state.orderCreateBase = { ...action.payload };
      }
    },
    updateDetailOrder: (
      state,
      action: PayloadAction<{
        type:
          | 'trahang'
          | 'thuno'
          | 'feetracking'
          | 'base'
          | 'error'
          | 'upgrade'
          | 'payfees'
          | 'replace'
          | 'dlgium'
          | 'chenhlech'
          | null;
        data: any[] | null;
      }>
    ) => {
      if (!action.payload.type) {
        // reset all
        state.detailBase = [];
        state.detailErrors = [];
        state.detailUpgrades = [];
        state.detailThuNos = [];
        state.detailChenhLechs = [];
        // state.productUpgrades = [];
        state.detailPayFees = [];
        state.detailReplace = [];
      }
      if (action.payload.type == 'base') {
        state.detailBase = action.payload.data;
      }
      if (action.payload.type == 'error') {
        state.detailErrors = action.payload.data;
      }
      if (action.payload.type == 'upgrade') {
        state.detailUpgrades = action.payload.data;
        // state.productUpgrades = action.payload.data;
      }

      if (action.payload.type == 'payfees') {
        state.detailPayFees = action.payload.data;
      }
      if (action.payload.type == 'dlgium') {
        state.detailDLLG = action.payload.data;
      }
      if (action.payload.type == 'trahang') {
        state.detailTraHang = action.payload.data;
      }
      if (action.payload.type == 'replace') {
        state.detailReplace = action.payload.data;
      }
      if (action.payload.type == 'thuno') {
        state.detailThuNos = action.payload.data;
      }
      if (action.payload.type == 'chenhlech') {
        state.detailChenhLechs = action.payload.data;
      }
    },
    pushNotifications: (state, action: PayloadAction<any>) => {
      state.notification = action.payload;
    },
    changeACNotify: (state, action: PayloadAction<boolean>) => {
      state.isACNotify = action.payload;
    },
    changeQActionOrder: (state, action: PayloadAction<boolean>) => {
      state.openQActionOrder = action.payload;
    },
    changeOpenUpdateOrder: (state, action: PayloadAction<boolean>) => {
      state.openUpdateOrder = action.payload;
    },
    changeCreateOrderByJson: (state, action: PayloadAction<any>) => {
      state.orderCreateByJson = action.payload;
    },
    updateOpenCreateOrderModal: (state, action: PayloadAction<boolean>) => {
      state.openCreateOrderModal = action.payload;
    },
    updateDonTaoLai: (state, action: PayloadAction<any>) => {
      if (action.payload.type === 'taolaidon') {
        state.donTaoLai = action.payload.data;
      } else if (action.payload.type === 'taocongduno') {
        state.taoCongThuNo = state.taoCongThuNo = {
          ...action.payload.data,
          text: action.payload.text
        };
      } else if (action.payload.type === 'taodonchietkhau') {
        state.taoDonChietKhau = state.taoDonChietKhau = {
          ...action.payload.data,
          text: action.payload.text
        };
      } else {
        state.donTaoLai = action.payload.data;
        state.taoDonChietKhau = null;
        state.taoCongThuNo = null; // hoặc initialState.taoCongThuNo
      }
    },
    updateOpenCodeJsonModal: (state, action: PayloadAction<boolean>) => {
      state.openCodeJsonModal = action.payload;
    },
    updateOpenDraftModal: (state, action: PayloadAction<boolean>) => {
      state.openCodeDraftModal = action.payload;
    },
    updateOpenCreateModal: (state, action: PayloadAction<boolean>) => {
      state.openCodeCreateModal = action.payload;
    },
    addDraftOrders: (state, action: PayloadAction<IOrderManager | any>) => {
      const orderSave: any = {
        ...action.payload,
        isNew: action.payload.maDonHang ? false : true,
        maDonSave: (state.draftOrders?.length || 0) + 1,
        timeSave: Helpers.ConvertDateToISOString(new Date())
      };
      const currentList = state.draftOrders || [];
      if (action.payload) state.draftOrders = [...currentList, orderSave];
    },
    addCreateOrders: (state, action: PayloadAction<IOrderManager | any>) => {
      // console.log('action', action.payload);
      if (action.payload) state.createOrders = action.payload;
    },
    removeDraftOrders: (state, action: PayloadAction<{ maDonHang?: string; clearAll?: boolean }>) => {
      if (action.payload.clearAll) {
        state.draftOrders = [];
        return;
      }
      const checkExist = state.draftOrders.findIndex(
        (draftOrder: IOrderManager) => draftOrder?.maDonSave === action.payload.maDonHang
      );
      state.draftOrders.splice(checkExist, 1);
    },
    addNotification: (state, action: PayloadAction<INotification>) => {
      const checkExist = state.listNotification.findIndex(note => note.maDonHang === action.payload.maDonHang);
      console.log('addNotification 0', checkExist);
      if (checkExist > -1) {
        // cập nhật mới nếu đã tồn tại cái cũ
        state.listNotification[checkExist] = { ...action.payload };
        return;
      }
      const notify = [action['payload']];
      const orderSave: INotification[] = [...state.listNotification, ...notify];
      state.listNotification = orderSave;

      console.log('addNotification 1', notify);
    },
    removeNotification: (state, action: PayloadAction<{ maDonHang?: string; clearAll?: boolean }>) => {
      if (action.payload.clearAll) {
        state.listNotification = [];
        return;
      }
      const checkExist = state.listNotification.findIndex(note => note.maDonHang === action.payload.maDonHang);
      state.listNotification.splice(checkExist, 1);
    },
    updateOpenHistoryNoti: (state, action: PayloadAction<boolean>) => {
      state.openHistoryNoti = !state.openHistoryNoti;
    },
    addHistoryNoti: (state, action: PayloadAction<INotification>) => {
      const checkExist = state.listHistoryNotis.findIndex(note => note.maDonHang === action.payload.maDonHang);
      // console.log('addNotification 0', checkExist);
      if (checkExist > -1) {
        // cập nhật mới nếu đã tồn tại cái cũ
        state.listHistoryNotis[checkExist] = { ...action.payload };
        return;
      }
      const notify = [action['payload']];
      const orderSave: INotification[] = [...state.listHistoryNotis, ...notify];
      state.listHistoryNotis = orderSave;
    },
    removeHistoryNoti: (state, action: PayloadAction<{ maDonHang?: string; clearAll?: boolean }>) => {
      if (action.payload.clearAll) {
        state.listHistoryNotis = [];
        return;
      }
      const checkExist = state.listHistoryNotis.findIndex(note => note.maDonHang === action.payload.maDonHang);
      state.listHistoryNotis.splice(checkExist, 1);
    },
    resetNotiHistory: state => {
      state.notiHistory = {
        items: [],
        pageIndex: 1,
        pageSize: 10,
        totalPages: 0,
        totalRecords: 0,
        hasNextPage: false,
        unreadCount: 0,
        loading: false,
        loadingMore: false
      };
    },

    openNotiHistoryModal: state => {
      state.notiHistoryModal!.open = true;
    },
    closeNotiHistoryModal: state => {
      state.notiHistoryModal!.open = false;
    },
    // (tuỳ chọn) chèn 1 thông báo mới lên đầu khi FCM đẩy xuống, không cần refetch
    prependNotiHistory: (state, action: PayloadAction<NotificationHistoryItem>) => {
      const h = state.notiHistory!;
      if (!h.items.some(i => i.id === action.payload.id)) {
        h.items.unshift(action.payload);
        h.totalRecords += 1;
        h.unreadCount = h.items && h.items?.filter(i => !i.isRead).length;
      }
    },

    updatePayMethod: (state, action: PayloadAction<any>) => {
      state.payMethod = action.payload;
    },
    updatePayedMoney: (state, action: PayloadAction<any>) => {
      state.payedMoney = action.payload;
    },
    updatePayedBankingMoney: (state, action: PayloadAction<any>) => {
      state.payedBankingMoney = action.payload;
    },
    updatePayWillMoney: (state, action: PayloadAction<any>) => {
      state.payWillMoney = action.payload;
    },
    updateVatValueMoney: (state, action: PayloadAction<any>) => {
      state.vatValueMoney = action.payload;
    },
    // updateAriseCost: (state, action: PayloadAction<any>) => {
    //   state.ariseCost = action.payload;
    // },
    updateOtherCost: (state, action: PayloadAction<any>) => {
      state.otherCost = action.payload;
    },
    updateTimePostPone: (state, action: PayloadAction<any>) => {
      state.timePostPone = action.payload;
    },
    updateSameCustomer: state => {
      const listOrder = allOrderAdapter.getSelectors().selectAll(state?.allOrderData);
      state.listSameCus = Helpers.CalSameCustomer(listOrder.map(order => order?.customerInfo?.maKhachHang));
    },
    updateResetCreateForm: (state, action: PayloadAction<any>) => {
      state.reqResetCreateForm = action.payload;
    },
    updateCodeProductQuick: (state, action: PayloadAction<string>) => {
      state.codeProductQuick = action.payload;
    },
    updateIsReqEditOrder: (state, action: PayloadAction<boolean>) => {
      state.isReqEditOrder = action.payload;
    },
    updateOMConfig: (state, action: PayloadAction<any>) => {
      state.omConfig = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(getDetailOfOrder.fulfilled, (state, action) => {
      const rltFormat = UtilOrder.formatOneDataOrder(action.payload?.data);
      // console.log('addCase getDetailOfOrder', action.payload?.data, action.payload?.forceOpen);
      if (!rltFormat) {
        console.log('Lỗi getDetailOfOrder..');
        return;
      }
      const updateValue = {
        id: rltFormat.maDonHang,
        changes: rltFormat
      };

      // chưa có thì thêm vô list vì thường là hợp lệ k trùng cũ
      allOrderAdapter.addOne(state.allOrderData, rltFormat);
      // nếu có đang chọn base thì cho cập nhật cái đang chọn theo base tạm chưa dùng
      if (state.orderCreateBase && rltFormat.maDonHang == state.orderCreateBase.maDonHang) {
        state.orderCreateBase = { ...rltFormat };
      }

      if (!state.orderSelect || action.payload?.forceOpen == true) {
        // chưa có chọn đơn hoặc là buộc update
        state.orderSelect = { ...rltFormat };
      } else if (state.orderSelect && rltFormat.maDonHang === state.orderSelect.maDonHang) {
        // có chọn đơn rồi
        state.orderSelect = { ...rltFormat };
      }
      // khi nào force thì bật modal lên
      if (action.payload?.forceOpen == true) {
        !state.openUpdateOrder && (state.openUpdateOrder = true);
      }

      // cập nhật 1 phần tử trong store
      action.payload = updateValue;
      orderManagerSlide.caseReducers.updateOneOrderData(state, action);
    });

    builder
      .addCase(getNotiHistory.pending, (state, action) => {
        const h = state.notiHistory!;
        const page = action.meta.arg?.pageIndex ?? 1;
        if (page > 1) h.loadingMore = true;
        else h.loading = true;
      })
      .addCase(getNotiHistory.fulfilled, (state, action: any) => {
        const h = state.notiHistory!;
        const data = action.payload;
        const isFirstPage = (data.pageIndex ?? 1) <= 1;
        h.items = isFirstPage ? data.items : [...h.items, ...data.items];
        h.pageIndex = data.pageIndex;
        h.pageSize = data._pageSize ?? h.pageSize;
        h.totalPages = data.totalPages;
        h.totalRecords = data.totalRecords;
        h.hasNextPage = data.hasNextPage;
        // Có số từ server thì giữ; chưa có (API lỗi / chưa gọi) mới đếm theo các trang đã tải
        if (!h.unreadFromServer) h.unreadCount = h.items.filter((i: NotificationHistoryItem) => !i.isRead).length;
        h.loading = false;
        h.loadingMore = false;
      })
      .addCase(getNotiHistory.rejected, state => {
        const h = state.notiHistory!;
        h.loading = false;
        h.loadingMore = false;
      })
      .addCase(readNoti.fulfilled, (state, action) => {
        const h = state.notiHistory!;
        const item = h.items.find(i => i.id === action.payload);
        if (item && !item.isRead) {
          item.isRead = true;
          h.unreadCount = Math.max(0, h.unreadCount - 1);
        }

        const mItem = state.notiHistoryModal?.items.find(i => i.id === action.payload);
        if (mItem) mItem.isRead = true;
      })
      .addCase(getUnreadNotiAmount.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.notiHistory!.unreadCount = action.payload;
        state.notiHistory!.unreadFromServer = true;
      })
      .addCase(readAllNotis.fulfilled, state => {
        const h = state.notiHistory!;
        h.items.forEach(i => (i.isRead = true));
        h.unreadCount = 0;
        state.notiHistoryModal?.items.forEach(i => (i.isRead = true));
      })
      .addCase(getNotiHistoryModal.pending, state => {
        state.notiHistoryModal!.loading = true;
      })
      .addCase(getNotiHistoryModal.fulfilled, (state, action: any) => {
        const m = state.notiHistoryModal!;
        const data = action.payload;
        m.items = data.items; // REPLACE, không nối
        m.pageIndex = data.pageIndex;
        m.pageSize = data._pageSize ?? m.pageSize;
        m.totalPages = data.totalPages;
        m.totalRecords = data.totalRecords;
        m.loading = false;
      })
      .addCase(getNotiHistoryModal.rejected, state => {
        state.notiHistoryModal!.loading = false;
      });
  }
});

export const allOrderSelector = allOrderAdapter.getSelectors((state: any) => state.orderManager.allOrderData);

export const {
  resetState,
  updateDataTableListOrder,
  updateListProductSelectFixed,
  updateCustomerSelect,
  updateCreateBaseOrder,
  resetDataListDataTable,
  updateReqReGetOrder,
  updateReqExportXls,
  updateAllOrderData,
  updateManyOrderData,
  updateOneOrderData,
  updateOneOrderOnlyData,
  addOneOrderInTopList,
  pushOrderInTopList,
  updateOrderSelected,
  forceUpdateOrderClick,
  updateDetailOrder,
  pushNotifications,
  changeACNotify,
  changeQActionOrder,
  changeOpenUpdateOrder,
  addNotification,
  addHistoryNoti,
  updateOpenHistoryNoti,
  changeCreateOrderByJson,
  updateOpenCreateOrderModal,
  updateOpenCodeJsonModal,
  updateOpenDraftModal,
  addDraftOrders,
  removeDraftOrders,
  removeNotification,
  removeHistoryNoti,
  updateCountOrder,
  updatePayMethod,
  updatePayedMoney,
  updatePayedBankingMoney,
  updatePayWillMoney,
  updateVatValueMoney,
  // updateAriseCost,
  updateOtherCost,
  reqCalculate,
  updateResetCreateForm,
  updateCodeProductQuick,
  updateIsReqEditOrder,
  updateSameCustomer,
  updateTimePostPone,
  //  new
  updateReqReGetCustomer,
  addOneCustomerInTopList,
  updateAllCustomerData,
  updateOpenCreateModal,
  addCreateOrders,
  updateDonTaoLai,
  updateOMConfig,
  resetNotiHistory,
  prependNotiHistory,
  openNotiHistoryModal,
  closeNotiHistoryModal
} = orderManagerSlide.actions;

export default orderManagerSlide.reducer;
